import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Box, Typography, Avatar, CircularProgress, useTheme, Tooltip, IconButton, Snackbar, Alert } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import StopIcon from '@mui/icons-material/Stop';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { useAppStore } from '@/store';
import { CotStep, savePartialMessage } from '@/api/messages';
import { useMessagesQuery } from '@/features/messages/hooks/useMessagesQuery';
import { useUpdateCurrentUserSettingsMutation } from '@/features/userSettings/hooks/useUserSettingsMutations';
import { ChatInput } from './ChatInput';
import { MessageActions } from './MessageActions';
import ArticleIcon from '@mui/icons-material/Article';
import ChainOfThought from './ChainOfThought';
import { ExportDownloadButton, parseAllExportReady, stripExportMarker } from './ExportDownloadButton';
import { Citation } from '@/types/citations';
import { MessageCitations } from './MessageCitations';

// Import image helper
import { getImageUrl } from '@/utils/image-helper';
import { getProfileImage, fetchAndCacheProfileImage } from '@/utils/profileImageCache';
import MainCards from './MainCards';
import AgentCards from './AgentCards';
import researcherCards from '@/config/researcherCards';
import DefaultWelcome from './DefaultWelcome';
import ResearcherWelcome from './ResearcherWelcome';
import { QueryEmptyState, QueryErrorState, QueryLoadingState, QueryStaleDataWarning } from './state';
import { queryKeys } from '@/lib/queryKeys';
import { invalidateConversationCollections } from '@/lib/queryInvalidation';

/**
 * During streaming, remark-gfm cannot parse a GFM table until it has received the
 * full separator row (|---|---|). Until then it renders the raw pipe characters as
 * plain text. This function detects an in-progress (incomplete) table at the END of
 * the streamed content and hides it until the block is complete. Text before the
 * incomplete table is returned unchanged so the rest of the response renders normally.
 *
 * A table block is considered complete once a separator row (|---|...) is present.
 */
function bufferIncompleteStreamingTable(content: string): string {
  const lines = content.split('\n');
  // Walk backwards to find where a pipe-based block starts
  let tableStartIdx = -1;
  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i].trim();
    if (line.startsWith('|') || line.match(/^\|?[-| :]+\|/)) {
      tableStartIdx = i;
    } else {
      break;
    }
  }
  if (tableStartIdx === -1) return content; // No trailing table block

  const tableLines = lines.slice(tableStartIdx);
  // A separator row contains only |, -, :, and spaces
  const hasSeparator = tableLines.some(l => /^\|?[\s|:\-]+\|/.test(l) && l.includes('-'));
  if (hasSeparator) return content; // Table is complete — render as-is

  // Table is incomplete — suppress it during streaming
  return lines.slice(0, tableStartIdx).join('\n');
}

// Define interfaces for message types
interface MessageDisplay {
  id: string;
  text: string;
  sender: string;
  timestamp: string;
  isProcessing?: boolean;
  citations?: Citation[];
}

export const ChatContainer: React.FC = () => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const selectedConversation = useAppStore(s => s.selectedConversation);
  // const conversationList = useAppStore(s => s.conversationList);
  const messages = useAppStore(s => s.messages);
  const setStoreMessages = useAppStore(s => s.setMessages);
  const selectedAgent = useAppStore(s => s.selectedAgent);
  const agentList = useAppStore(s => s.agentList);
  const user = useAppStore(s => s.user); // Get user info for avatar
  const setStoreSelectedAgent = useAppStore(s => s.setSelectedAgent);
  const selectedModel = useAppStore(s => s.selectedModel);
  const thinkingMode = useAppStore(s => s.thinkingMode);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const previousConversationIdRef = useRef<string | null>(null);
  
  // Add global styles for citation superscripts
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      sup {
        line-height: 0;
        font-size: 0.75em;
        vertical-align: super;
        color: ${theme.palette.primary.main};
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, [theme.palette.primary.main]);
  
  // Load profile image from localStorage cache (same as Header.tsx)
  const [cachedProfileImage, setCachedProfileImage] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    const ensureImage = async () => {
      if (!user?.id) { setCachedProfileImage(null); return; }
      const cached = getProfileImage(user.id);
      if (cached) {
        if (!cancelled) setCachedProfileImage(cached);
      } else {
        const fetched = await fetchAndCacheProfileImage(user.id, false);
        if (!cancelled) setCachedProfileImage(fetched || null);
      }
    };
    ensureImage().catch(err => console.error('Failed to load profile image in chat:', err));
    return () => { cancelled = true; };
  }, [user?.id]);
  const userPictureUrl = cachedProfileImage || undefined;
  
  // Streaming state management
  // activeStreamConvIds tracks which conversations are currently streaming (supports parallel streams).
  const [activeStreamConvIds, setActiveStreamConvIds] = useState<Set<string>>(new Set());
  // Maps convId → { controller, cancelledRef } — one entry per active stream for abort control.
  const activeStreamsRef = useRef<Map<string, { controller: AbortController; cancelledRef: { current: boolean } }>>(new Map());
  // Stores the latest partial message per convId so the UI can reconnect when switching conversations.
  const streamingMessagesMapRef = useRef<Map<string, string>>(new Map());
  // Snapshot of [existingMessages + userMessage] taken at stream-start time, keyed by convId.
  // Used to restore the correct message list when the user navigates back to a streaming conversation.
  const streamingBaseMessagesRef = useRef<Map<string, any[]>>(new Map());

  // Display state — reflects the *currently viewed* conversation's live stream only.
  const [streamingMessage, setStreamingMessage] = useState<string>('');
  const [finalStreamMessage, setFinalStreamMessage] = useState<string>('');
  const [streamingCancelledForUI, setStreamingCancelledForUI] = useState(false); // State for UI rendering
  const [isLoadingSavedMessages, setIsLoadingSavedMessages] = useState(false);
  const [streamingCitations, setStreamingCitations] = useState<Citation[]>([]);
  
  // Chain of Thought state management
  const [cotSteps, setCotSteps] = useState<CotStep[]>([]);
  const cotStepsRef = useRef<CotStep[]>([]); // Ref mirror to avoid stale closure in handleStreamDone
  const [isCotExpanded, setIsCotExpanded] = useState(true);
  const [isCotComplete, setIsCotComplete] = useState(false);
  // Holds the completed CoT steps after streaming ends so the user can re-open them
  const [completedCotSteps, setCompletedCotSteps] = useState<CotStep[]>([]);
  const [isCompletedCotExpanded, setIsCompletedCotExpanded] = useState(false);
  
  // Reasoning model CoT tracking — now created per-stream inside addUserMessage.
  // updateReasoningCotStep is kept here because it only mutates display state when called
  // from the currently-viewed conversation's stream.
  const updateCurrentUserSettingsMutation = useUpdateCurrentUserSettingsMutation();
  
  // State for copy button
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  
  // Drag and drop state
  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = useRef(0); // Counts nested dragenter/dragleave to avoid stuck overlay
  const chatInputRef = useRef<{ handleDroppedFiles: (files: File[]) => void } | null>(null);
  
  // Snackbar state for feedback notifications
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const {
    data: queriedMessages,
    isPending: isMessagesPending,
    isFetching: isMessagesFetching,
    isError: isMessagesError,
    isRefetchError: isMessagesRefetchError,
    error: messagesQueryError,
    refetch: refetchMessages,
  } = useMessagesQuery(selectedConversation?.id);

  const messagesQueryErrorMessage = isMessagesError
    ? (messagesQueryError instanceof Error ? messagesQueryError.message : 'Failed to load messages')
    : null;
  const hasCachedMessages = messages.length > 0;
  // True only when the *currently viewed* conversation is the one being streamed.
  // Used to scope streaming-UI restrictions (disabled input, hidden prompt cards) so that
  // switching to a new chat while a background stream is running does not lock the new chat.
  const isStreamingCurrentConversation = activeStreamConvIds.has(selectedConversation?.id ?? '');

  const showMessagesStaleWarning = Boolean(
    isMessagesRefetchError && messagesQueryErrorMessage && hasCachedMessages && !isStreamingCurrentConversation
  );
  const blockingMessagesError = hasCachedMessages ? null : messagesQueryErrorMessage;

  const retryMessagesQuery = async () => {
    if (!selectedConversation?.id) {
      return;
    }

    await queryClient.invalidateQueries({
      queryKey: queryKeys.conversations.messages(selectedConversation.id),
    });
  };
  
  const handleShowSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };
  
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };
  
  // Stop streaming function
  const stopStreaming = () => {
    const convId = selectedConversation?.id;
    if (!convId) return;
    const stream = activeStreamsRef.current.get(convId);
    if (stream) {
      stream.cancelledRef.current = true;
      stream.controller.abort();
    }
    setStreamingCancelledForUI(true);
  };

  // Get the current agent name
  const getCurrentAgentName = () => {
    const agent = agentList.find(a => a.id === selectedAgent);
    return agent ? agent.name : 'CHAT';
  };

  useEffect(() => {
    // When the selected conversation is cleared (e.g. after bulk delete from Search page),
    // clear the message list so the chat pane doesn't show the previous conversation's content.
    if (!selectedConversation?.id) {
      setStoreMessages([]);
      previousConversationIdRef.current = null;
      return;
    }

    const previousConversationId = previousConversationIdRef.current;
    const hasConversationChanged = previousConversationId !== selectedConversation.id;
    previousConversationIdRef.current = selectedConversation.id;

    if (hasConversationChanged) {
      // Always clear CoT UI state when moving between conversations.
      setCotSteps([]);
      cotStepsRef.current = [];
      setIsCotComplete(false);
      setIsCotExpanded(true);
      setCompletedCotSteps([]);
      setIsCompletedCotExpanded(false);

      if (activeStreamConvIds.has(selectedConversation.id)) {
        // The target conversation has an active background stream.
        // Restore the pre-stream message snapshot + a placeholder so the user sees
        // the correct user query and the live assistant response as tokens arrive.
        const baseMessages = streamingBaseMessagesRef.current.get(selectedConversation.id) ?? [];
        setStoreMessages([
          ...baseMessages,
          {
            id: `streaming-placeholder-${selectedConversation.id}`,
            text: '',
            created_by: 'assistant',
            created_at: new Date().toISOString(),
            conversation_id: selectedConversation.id,
          },
        ]);
        setIsLoadingSavedMessages(false);
      } else {
        // Normal switch to a non-streaming conversation: clear and fetch from backend.
        setStoreMessages([]);
        setIsLoadingSavedMessages(true);
      }
    } else {
      setIsLoadingSavedMessages(false);
    }

    // Update selected agent based on conversation's agent_key
    if (selectedConversation.agent_key && selectedConversation.agent_key !== selectedAgent) {
      setStoreSelectedAgent(selectedConversation.agent_key);
    }
  }, [
    activeStreamConvIds,
    selectedConversation?.id,
    selectedConversation?.agent_key,
    selectedAgent,
    setStoreMessages,
    setStoreSelectedAgent,
  ]);

  useEffect(() => {
    if (isStreamingCurrentConversation) {
      return;
    }

    if (queriedMessages) {
      setStoreMessages(queriedMessages);
    }

    if (!isMessagesPending && !isMessagesFetching) {
      setIsLoadingSavedMessages(false);
    }
  }, [queriedMessages, isMessagesPending, isMessagesFetching, activeStreamConvIds, setStoreMessages]);

  // When switching TO a conversation whose stream is still running in the background,
  // restore its current partial message to the display state so the user can follow along.
  useEffect(() => {
    const convId = selectedConversation?.id;
    if (!convId) return;
    if (!activeStreamConvIds.has(convId)) return;
    const partial = streamingMessagesMapRef.current.get(convId) ?? '';
    setStreamingMessage(partial);
    setFinalStreamMessage(partial);
    // Citations for this background stream are not kept live, but the message itself is.
  }, [selectedConversation?.id, activeStreamConvIds]);

  useEffect(() => {
    // Only scroll to bottom for streaming content
    if (isStreamingCurrentConversation) {
      const timeoutId = setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [streamingMessage, isStreamingCurrentConversation]);

  // Separate useEffect specifically for handling saved message loading
  useEffect(() => {
    if (!isStreamingCurrentConversation && isLoadingSavedMessages && messages.length > 0) {
      // Use a longer delay to ensure all messages are fully rendered
      const timeoutId = setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
        // Reset only the loading flag, but keep viewing saved messages active
        setTimeout(() => {
          setIsLoadingSavedMessages(false);
        }, 50);
      }, 10);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isLoadingSavedMessages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Helper to update reasoning CoT step
  const updateReasoningCotStep = useCallback((thinkingText: string, status: 'in_progress' | 'completed') => {
    const reasoningStep: CotStep = {
      type: 'cot_step',
      step_id: 'reasoning',
      title: 'Reasoning',
      description: thinkingText.trim(),
      status: status
    };
    
    setCotSteps(prevSteps => {
      const existingIndex = prevSteps.findIndex(s => s.step_id === 'reasoning');
      let nextSteps: CotStep[];
      if (existingIndex >= 0) {
        nextSteps = [...prevSteps];
        nextSteps[existingIndex] = reasoningStep;
      } else {
        // Add reasoning step at the beginning
        nextSteps = [reasoningStep, ...prevSteps];
      }
      cotStepsRef.current = nextSteps; // Keep ref in sync
      return nextSteps;
    });
  }, []);

  // loadMessages and addUserMessage are defined below.
  // All per-stream callbacks live inside addUserMessage as closures so each stream
  // has its own isolated state and never contaminates another conversation's data.

  const loadMessages = async (conversationId: string) => {
    if (!conversationId) {
      return;
    }

    setIsLoadingSavedMessages(true);
    // Reset streaming cancelled state when loading a new conversation
    // This prevents showing the cancelled indicator on messages that weren't cancelled
    setStreamingCancelledForUI(false);
    // Clear completed CoT — it belongs to the previous conversation, not this one
    setCompletedCotSteps([]);
    setIsCompletedCotExpanded(false);
  
    try {
      const result = await refetchMessages();
      const msgs = result.data ?? [];
      setStoreMessages(msgs);
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setIsLoadingSavedMessages(false);
    }
  };

  const addUserMessage = async (text: string, files: File[]) => {
    if (!selectedConversation?.id) return;

    // --- Capture values at call-time so closures are stable ---
    const convId = selectedConversation.id;
    const modelUsed = selectedModel;
    const agentUsed = selectedAgent;

    // Per-stream LOCAL state — completely isolated from any other concurrent stream.
    const streamMsgRef = { current: '' };
    const streamRawRef = { current: '' };
    const streamThinkingRef = { current: '' };
    const streamIsInsideThinkRef = { current: false };
    const streamCancelledRef = { current: false };
    let streamCitationsLocal: Citation[] = [];
    const streamCotStepsRef = { current: [] as CotStep[] };

    // Helper: is this stream's conversation currently displayed?
    const isCurrentConv = () => useAppStore.getState().selectedConversation?.id === convId;

    // Persist current data sources to conversation BEFORE streaming starts
    const currentDs = useAppStore.getState().dataSources;
    if (currentDs) {
      try {
        const { updateConversation } = await import('../api/conversations');
        await updateConversation(convId, { data_sources: currentDs } as any);
      } catch (err) {
        console.error('Failed to persist data sources before send:', err);
      }
    }

    // Add user message + streaming placeholder to UI immediately
    const now = new Date().toISOString();
    const newMessage = {
      id: `temp-${Date.now()}`,
      text,
      created_by: 'user',
      created_at: now,
      contains_attachment: files.length > 0,
      file_names: files.length > 0 ? files.map(f => f.name) : undefined,
    };
    const streamingPlaceholder = {
      id: `streaming-${Date.now()}`,
      text: '',
      created_by: 'assistant',
      created_at: now,
      conversation_id: convId,
    };
    setStoreMessages([...messages, newMessage, streamingPlaceholder]);

    // Register stream and update active-stream set
    const abortController = new AbortController();
    activeStreamsRef.current.set(convId, { controller: abortController, cancelledRef: streamCancelledRef });
    streamingMessagesMapRef.current.set(convId, '');
    // Snapshot messages at stream-start (user message already appended, placeholder NOT included).
    // This lets us restore the correct context when the user navigates back mid-stream.
    streamingBaseMessagesRef.current.set(convId, [...messages, newMessage]);
    setActiveStreamConvIds(prev => new Set([...prev, convId]));

    // Clear display state only if this is the currently viewed conversation
    if (isCurrentConv()) {
      setStreamingMessage('');
      setFinalStreamMessage('');
      setStreamingCancelledForUI(false);
      setCotSteps([]);
      cotStepsRef.current = [];
      setIsCotComplete(false);
      setIsCotExpanded(true);
      setCompletedCotSteps([]);
      setIsCompletedCotExpanded(false);
      setStreamingCitations([]);
    }

    // ---- Per-stream callbacks ----

    const handleStreamToken = (token: string) => {
      streamRawRef.current += token;
      const rawStream = streamRawRef.current;

      // <think> opening tag
      if (!streamIsInsideThinkRef.current && rawStream.includes('<think>')) {
        streamIsInsideThinkRef.current = true;
        const beforeThink = rawStream.split('<think>')[0];
        if (beforeThink && !streamMsgRef.current.includes(beforeThink)) {
          streamMsgRef.current = beforeThink;
        }
        const afterOpenTag = rawStream.split('<think>')[1] || '';
        if (!afterOpenTag.includes('</think>')) {
          streamThinkingRef.current = afterOpenTag;
          if (isCurrentConv()) updateReasoningCotStep(afterOpenTag, 'in_progress');
        }
        if (isCurrentConv()) {
          setStreamingMessage(streamMsgRef.current);
          setFinalStreamMessage(streamMsgRef.current);
          streamingMessagesMapRef.current.set(convId, streamMsgRef.current);
          setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }), 50);
        }
        return;
      }

      // </think> closing tag
      if (streamIsInsideThinkRef.current && rawStream.includes('</think>')) {
        streamIsInsideThinkRef.current = false;
        const thinkMatch = rawStream.match(/<think>([\s\S]*?)<\/think>/);
        if (thinkMatch) {
          streamThinkingRef.current = thinkMatch[1].trim();
          if (isCurrentConv()) updateReasoningCotStep(streamThinkingRef.current, 'completed');
        }
        const afterThink = rawStream.split('</think>')[1] || '';
        streamMsgRef.current = afterThink.trimStart();
        if (isCurrentConv()) {
          setStreamingMessage(streamMsgRef.current);
          setFinalStreamMessage(streamMsgRef.current);
          streamingMessagesMapRef.current.set(convId, streamMsgRef.current);
          if (streamMsgRef.current.endsWith('\n')) scrollToBottom();
        }
        return;
      }

      // Inside think tags — update thinking content
      if (streamIsInsideThinkRef.current) {
        const afterOpenTag = rawStream.split('<think>')[1] || '';
        streamThinkingRef.current = afterOpenTag;
        if (isCurrentConv()) updateReasoningCotStep(afterOpenTag, 'in_progress');
        return;
      }

      // Normal token (no think tags)
      if (rawStream.includes('</think>')) {
        streamMsgRef.current = rawStream.split('</think>')[1]?.trimStart() ?? '';
      } else if (!rawStream.includes('<think>')) {
        streamMsgRef.current += token;
      }
      streamingMessagesMapRef.current.set(convId, streamMsgRef.current);

      if (isCurrentConv()) {
        setStreamingMessage(streamMsgRef.current);
        setFinalStreamMessage(streamMsgRef.current);
        if (streamMsgRef.current.endsWith('\n')) scrollToBottom();
      }
    };

    const handleCotStep = (step: CotStep) => {
      // Update per-stream CoT ref
      const existingIndex = streamCotStepsRef.current.findIndex(s => s.step_id === step.step_id);
      if (existingIndex >= 0) {
        streamCotStepsRef.current = [...streamCotStepsRef.current];
        streamCotStepsRef.current[existingIndex] = { ...streamCotStepsRef.current[existingIndex], ...step };
      } else {
        streamCotStepsRef.current = [...streamCotStepsRef.current, step];
      }
      // Update display only for the currently viewed conversation
      if (isCurrentConv()) {
        setCotSteps(prev => {
          const idx = prev.findIndex(s => s.step_id === step.step_id);
          if (idx >= 0) {
            const next = [...prev];
            next[idx] = { ...prev[idx], ...step };
            cotStepsRef.current = next;
            return next;
          }
          const next = [...prev, step];
          cotStepsRef.current = next;
          return next;
        });
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }), 50);
      }
    };

    const handleCitations = (citations: Citation[]) => {
      streamCitationsLocal = citations;
      if (isCurrentConv()) setStreamingCitations(citations);
    };

    const handleStreamDone = async () => {
      const messageToSave = streamMsgRef.current;

      // Finalise CoT for the live view if this is the current conversation
      if (isCurrentConv() && streamCotStepsRef.current.length > 0) {
        setIsCotComplete(true);
        const snapshotSteps = [...streamCotStepsRef.current];
        setTimeout(() => {
          setIsCotExpanded(false);
          setCompletedCotSteps(snapshotSteps);
          setIsCompletedCotExpanded(false);
        }, 1000);
      }

      // --- Persist the message ---
      if (messageToSave.trim() && convId) {
        if (streamCancelledRef.current) {
          // Partial/cancelled save
          try {
            const savedMessage = await savePartialMessage(
              convId,
              messageToSave,
              agentUsed || undefined,
              modelUsed || undefined,
              thinkingMode,
            );
            if (isCurrentConv()) {
              const currentMessages = useAppStore.getState().messages;
              const updatedMessages = currentMessages.map(msg =>
                msg.created_by === 'assistant' && msg.id?.startsWith('streaming-')
                  ? { ...savedMessage, citations: streamCitationsLocal.length > 0 ? streamCitationsLocal : undefined, was_cancelled: true }
                  : msg
              );
              setStoreMessages(updatedMessages);
              scrollToBottom();
            }
            await invalidateConversationCollections(queryClient);
          } catch (error) {
            console.error('Failed to save partial message:', error);
            if (isCurrentConv()) {
              const currentMessages = useAppStore.getState().messages;
              const updatedMessages = currentMessages.map(msg =>
                msg.created_by === 'assistant' && msg.id?.startsWith('streaming-')
                  ? { ...msg, text: messageToSave, citations: streamCitationsLocal.length > 0 ? streamCitationsLocal : undefined }
                  : msg
              );
              setStoreMessages(updatedMessages);
              scrollToBottom();
            }
          }
        } else {
          // Normal completion — backend already saved; reload messages for this conv
          try {
            await new Promise(resolve => setTimeout(resolve, 500));
            // Always invalidate so list ordering/preview updates
            await invalidateConversationCollections(queryClient);
            // Reload messages — if viewing this conv, updates the store directly;
            // otherwise the query cache is refreshed for when user navigates back.
            if (isCurrentConv()) {
              await loadMessages(convId);
              scrollToBottom();
            } else {
              // Refresh the query cache for this conversation even though it's not visible
              await queryClient.invalidateQueries({
                queryKey: queryKeys.conversations.messages(convId),
              });
            }
          } catch (error) {
            console.error('Failed to reload messages:', error);
            if (isCurrentConv()) {
              const currentMessages = useAppStore.getState().messages;
              setStoreMessages([...currentMessages, {
                id: `assistant-${Date.now()}`,
                text: messageToSave,
                created_by: 'assistant',
                created_at: new Date().toISOString(),
                agent_key: agentUsed || undefined,
                conversation_id: convId,
              }]);
            }
          }
        }
      } else if (!messageToSave.trim() && streamCancelledRef.current && convId) {
        // Cancelled before any tokens arrived
        try {
          const savedMessage = await savePartialMessage(
            convId,
            '',
            agentUsed || undefined,
            modelUsed || undefined,
            thinkingMode,
          );
          if (isCurrentConv()) {
            const currentMessages = useAppStore.getState().messages;
            setStoreMessages(currentMessages.map(msg =>
              msg.created_by === 'assistant' && msg.id?.startsWith('streaming-')
                ? { ...savedMessage, was_cancelled: true }
                : msg
            ));
          }
          await invalidateConversationCollections(queryClient);
        } catch (error) {
          console.error('Failed to save cancelled empty message:', error);
          if (isCurrentConv()) {
            const currentMessages = useAppStore.getState().messages;
            setStoreMessages(currentMessages.map(msg =>
              msg.created_by === 'assistant' && msg.id?.startsWith('streaming-')
                ? { ...msg, text: '', was_cancelled: true }
                : msg
            ));
          }
        }
      } else if (!messageToSave.trim()) {
        // Error case — remove empty placeholder
        if (isCurrentConv()) {
          const currentMessages = useAppStore.getState().messages;
          setStoreMessages(currentMessages.filter(msg =>
            !(msg.created_by === 'assistant' && !msg.text?.trim())
          ));
        }
      }

      // Persist last used model (non-blocking)
      if (modelUsed && convId) {
        try {
          const { updateConversation } = await import('../api/conversations');
          const currentState = useAppStore.getState();
          const userId = currentState.userSettings?.id;
          await updateConversation(convId, { last_used_model: modelUsed });
          const ds = currentState.dataSources;
          if (ds) await updateConversation(convId, { data_sources: ds } as any);
          if (userId) {
            await updateCurrentUserSettingsMutation.mutateAsync({ default_model: modelUsed });
            if (currentState.userSettings) {
              currentState.setUserSettings({ ...currentState.userSettings, default_model: modelUsed });
            }
          }
        } catch (error) {
          console.error('Failed to save last used model:', error);
        }
      }

      // De-register stream
      activeStreamsRef.current.delete(convId);
      streamingMessagesMapRef.current.delete(convId);
      streamingBaseMessagesRef.current.delete(convId);
      setActiveStreamConvIds(prev => {
        const next = new Set(prev);
        next.delete(convId);
        return next;
      });

      // Clear display state only if this is still the viewed conversation
      if (isCurrentConv()) {
        setStreamingMessage('');
        setFinalStreamMessage('');
        // Don't reset streamingCancelledForUI here — needed to show cancellation indicator
        setCotSteps([]);
        cotStepsRef.current = [];
        setIsCotComplete(false);
        setIsCotExpanded(true);
      }
    };

    // ---- Start streaming ----
    try {
      const { postStreamingMessage } = await import('../api/messages');
      await postStreamingMessage(
        convId,
        {
          text,
          model_key: modelUsed || undefined,
          agent_key: agentUsed || undefined,
          thinking_mode: thinkingMode !== 'auto' ? thinkingMode : undefined,
          files,
        },
        handleStreamToken,
        async () => { await handleStreamDone(); },
        (error) => {
          console.error('Streaming error:', error);
          // De-register stream on error
          activeStreamsRef.current.delete(convId);
          streamingMessagesMapRef.current.delete(convId);
          streamingBaseMessagesRef.current.delete(convId);
          setActiveStreamConvIds(prev => {
            const next = new Set(prev);
            next.delete(convId);
            return next;
          });
          if (isCurrentConv()) {
            setStreamingCancelledForUI(false);
            setCotSteps([]);
            cotStepsRef.current = [];
            setIsCotComplete(false);
          }
        },
        handleCotStep,
        handleCitations,
        abortController.signal,
      );
    } catch (error) {
      console.error('Failed to start streaming:', error);
      activeStreamsRef.current.delete(convId);
      streamingMessagesMapRef.current.delete(convId);
      streamingBaseMessagesRef.current.delete(convId);
      setActiveStreamConvIds(prev => {
        const next = new Set(prev);
        next.delete(convId);
        return next;
      });
    }
  };
  
  // Process message content for display
  const processMessageContent = (message: any): MessageDisplay => {
    const result: MessageDisplay = {
      id: message.id,
      text: message.text,
      sender: message.created_by,
      timestamp: message.created_at,
      isProcessing: !!message.processing_status,
    };

    // Check for processing status placeholder
    if (message.processing_status) {
      return result;
    }
    
    // Strip <think>...</think> tags from reasoning models (e.g., DeepSeek)
    if (result.text && result.text.includes('<think>')) {
      result.text = result.text.replace(/<think>[\s\S]*?<\/think>/g, '').trim();
    }
    
    // Handle citations - prefer new structured format from backend
    if (message.citations && Array.isArray(message.citations) && message.citations.length > 0) {
      // Use new structured citations directly from backend
      result.citations = message.citations;
    } else if (message.file_names && Array.isArray(message.file_names) && message.file_names.length > 0) {
      // Backwards compatibility: convert file_names to simple citations
      result.citations = message.file_names.map((filename: string) => ({
        type: 'document' as const,
        filename,
        snippet: `Referenced document: ${filename}`
      }));
    } else if (result.text.includes('Citations:') || result.text.includes('### Source Documents:')) {
      // Legacy format: parse from text
      let citationsPart = result.text.split('Citations:');
      
      if (citationsPart.length === 1) {
        citationsPart = result.text.split('### Source Documents:');
      }
      
      if (citationsPart.length > 1) {
        // Get main content without citations
        result.text = citationsPart[0].trim();
        
        // Extract citations
        const citationText = citationsPart[1].trim();
        const citationLines = citationText.split('\n')
          .filter(line => line.trim().startsWith('-'));
        
        const processedLines = citationLines.map(line => line.replace(/^-\s*/, '').trim());
        
        result.citations = processedLines.map(file => ({
          type: 'document' as const,
          filename: file,
          snippet: `Referenced document: ${file}`
        }));
      }
    }
    
    // Always remove citation text from display if present
    // Match various formats: "Citations:", "**Citations**", "## Citations", "### Citations"
    result.text = result.text.replace(/\n+(?:\*{2}Citations\*{2}|#{1,3}\s*Citations)[\s\S]*$/i, '').trim();
    if (result.text.includes('Citations:')) {
      result.text = result.text.split('Citations:')[0].trim();
    }
    if (result.text.includes('### Source Documents:')) {
      result.text = result.text.split('### Source Documents:')[0].trim();
    }
    
    // Strip inline citation markers from response text (Sources section shows the actual references)
    // Remove [EXCERPT N] or [EXCERPT N from filename] patterns
    result.text = result.text.replace(/\[EXCERPT\s*\d+(?:\s+from\s+[^\]]+)?\]/gi, '');
    // Remove [1], [2], [3] style numeric citations (including superscript-like ones)
    result.text = result.text.replace(/\[\d+\]/g, '');
    // Remove (ref1), (ref2) style citations
    result.text = result.text.replace(/\(ref\d+\)/gi, '');
    // Remove trailing orphaned markdown markers like ** at the end
    result.text = result.text.replace(/\*{2,}\s*$/g, '');
    // Trim leading/trailing whitespace at message boundaries.
    // NOTE: Do NOT collapse inline multiple spaces here – markdown nested lists use
    // 2-space indentation ("  - child") and collapsing them flattens the list to top-level.
    result.text = result.text.trim();

    return result;
  };

  // Renders attached files with optional OneDrive links
  const renderAttachedFiles = (fileNames: string[], onedriveUrls?: (string | null)[]) => (
    <Box mt={0.75}>
      {fileNames.map((fileName, index) => {
        const onedriveUrl = onedriveUrls?.[index];
        return (
          <Box key={index} display="flex" alignItems="center" mt={0.25}>
            <ArticleIcon fontSize="small" sx={{ mr: 1, color: 'primary.main', opacity: 0.7 }} />
            {onedriveUrl ? (
              <Typography
                component="a"
                href={onedriveUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                sx={{
                  color: theme.palette.mode === 'light' ? 'primary.main' : 'primary.light',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {fileName}
              </Typography>
            ) : (
              <Typography variant="body2" color={theme.palette.mode === 'light' ? "text.primary" : "rgba(255, 255, 255, 0.9)"}>
                {fileName}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );

  // RH fix
  // Fixes newline rendering issues in DeepSeek responses and markdown tables
  // Also preserves single newlines by converting them to double newlines (Markdown line breaks)
  // Note: HTML <br> tags are now handled by rehype-raw plugin
  const replaceNewlines = (str: string) => {
    // console.log('Original string:\n', str);
    
    // Protect code blocks first
    let result = str;
    const codeBlocks: string[] = [];
    result = result.replace(/(```[\s\S]*?```)/g, (match) => {
      codeBlocks.push(match);
      return `__CODEBLOCK_${codeBlocks.length - 1}__`;
    });
    
    // Protect table blocks — a table is a contiguous run of lines starting with |
    // Capture the entire block so replaceNewlines doesn't double newlines between rows.
    const tableBlocks: string[] = [];
    result = result.replace(/((?:^|\n)(?:\|[^\n]*\n?)+)/g, (match) => {
      tableBlocks.push(match);
      return `__TABLEBLOCK_${tableBlocks.length - 1}__`;
    });
    
    // Don't double newlines that are already doubled or part of lists
    // Replace single newlines with double, but not if:
    // - Already followed by another newline
    // - After a list item (lines starting with -)
    // - Before a digit (numbered list item)
    // - Before a markdown link [
    // - Before an indented continuation line (3+ spaces = list item content)
    result = result.replace(/([^\n])\n(?!\n)(?!\s*-)(?!\d)(?!\s*\[)(?!\s{3,})/g, '$1\n\n');
    
    // Restore table blocks
    tableBlocks.forEach((block, index) => {
      result = result.replace(`__TABLEBLOCK_${index}__`, block);
    });
    
    // Restore code blocks
    codeBlocks.forEach((block, index) => {
      result = result.replace(`__CODEBLOCK_${index}__`, block);
    });
    
    // console.log('Processed string:', result);
    return result;
  };

  // Copy code to clipboard
  const handleCopyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  // Helper function to get date string from timestamp
  const getDateString = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  // Helper function to get time string from timestamp (for message bubbles)
  // No explicit timeZone is set so JavaScript uses the browser's OS/local timezone automatically,
  // which is correct for users in any region (CH, DE, CZ, US, Asia, etc.).
  const getTimeString = (timestamp: string): string => {
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) {
        console.warn('Invalid timestamp:', timestamp);
        return 'Invalid time';
      }
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit', 
        hour12: true
        // No timeZone option → browser uses the user's local OS timezone
      });
    } catch (error) {
      console.error('Error formatting time:', timestamp, error);
      return 'Error';
    }
  };

  // Helper function to check if date changed between two timestamps
  const hasDateChanged = (prevTimestamp: string, currentTimestamp: string): boolean => {
    if (!prevTimestamp) return false;
    return getDateString(prevTimestamp) !== getDateString(currentTimestamp);
  };

  // Drag and drop handlers for full chat area
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isStreamingCurrentConversation) return;
    dragCounterRef.current++;
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragging(false);

    if (isStreamingCurrentConversation) return;

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0 && chatInputRef.current) {
      chatInputRef.current.handleDroppedFiles(droppedFiles);
    }
  };

  if (!selectedConversation) {
    return null;
  }
  
  return (    <Box 
      display="flex" 
      flexDirection="column" 
      height="100%"
      position="relative"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      sx={{
        bgcolor: theme.palette.mode === 'light' ? 'background.default' : 'background.default',
        backdropFilter: 'blur(10px)',
        alignItems: 'center', // Center the content horizontally
        px: 2 // Reduced horizontal padding
      }}
    >
      {/* Drag and drop overlay */}
      {isDragging && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.palette.mode === 'light'
              ? 'rgba(255, 255, 255, 0.95)'
              : 'rgba(0, 0, 0, 0.85)',
            backdropFilter: 'blur(8px)',
            zIndex: 9999,
            pointerEvents: 'none'
          }}
        >
          <Box
            sx={{
              textAlign: 'center',
              border: `3px dashed ${theme.palette.primary.main}`,
              borderRadius: '24px',
              padding: { xs: '24px 20px', sm: '40px 48px', md: '64px 96px' },
              backgroundColor: theme.palette.mode === 'light'
                ? 'rgba(0, 122, 195, 0.05)'
                : 'rgba(0, 122, 195, 0.15)'
            }}
          >
            <AttachFileIcon sx={{ fontSize: { xs: 48, sm: 64, md: 80 }, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" color="primary.main" fontWeight={600} sx={{ mb: 1, fontSize: { xs: '1.25rem', sm: '1.6rem', md: '2rem' } }}>
              Drop files here
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              Maximum 5 files • PDF, DOCX, DOC, TXT, PNG, JPG, JPEG, GIF, WEBP
            </Typography>
          </Box>
        </Box>
      )}
      {/* Container to limit max width and center content */}
      <Box
        sx={{
          width: '100%',
          maxWidth: '1170px', // Limit maximum width
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          py: 6 // Add vertical padding
        }}
      >      {/* Agent Header - moved to the top with fixed position */}
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          height: 28,
          pl: { xs: 5, sm: 2 },
          backgroundColor: theme.palette.mode === 'light' ? 'background.default' : 'background.default',
          position: 'sticky',
          top: 0,
          zIndex: 10,
          paddingBottom: 2,
          justifyContent: 'flex-end'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, maxWidth: '100%' }}>
          <Typography 
            variant="subtitle1" 
            fontWeight={600} 
            color="primary.main" 
            sx={{ 
              lineHeight: 1,
              display: 'flex',
              alignItems: 'center',
              m: 0,
              p: 0,
              flexShrink: 0,
              whiteSpace: 'nowrap'
            }}
          >
            {getCurrentAgentName()}
          </Typography>
          <Typography 
            variant="subtitle1" 
            color="text.secondary" 
            sx={{ mx: 0.5, flexShrink: 0 }}
          >
            ›
          </Typography>
          <Typography 
            variant="subtitle1" 
            color="text.primary"
            sx={{ 
              lineHeight: 1,
              m: 0,
              p: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
              flex: 1,
              pr: 0.25
            }}
          >
            {selectedConversation?.title || 'Untitled Chat'}
          </Typography>
        </Box>
      </Box>
      <Box 
        ref={scrollContainerRef}
        data-print-messages
        flex={1} 
        overflow="auto" 
        mb={17} // Margin bottom for input space - ensures scrollbar is fully visible
        sx={{ 
          scrollBehavior: 'smooth',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: theme.palette.mode === 'light'
              ? 'rgba(0, 0, 0, 0.2)'
              : 'rgba(255, 255, 255, 0.35)',  // Increased opacity to match the sidebar scrollbar
            borderRadius: '4px',
          },
          paddingTop: '16px', // Add padding at the top to separate from header
          paddingBottom: '16px', // Padding at the bottom for message actions
          paddingRight: '8px', // Add padding on the right to create space between avatars and scrollbar
        }}
      >
        {showMessagesStaleWarning ? (
          <Box px={2} pb={1}>
            <QueryStaleDataWarning
              message="Showing last synced data. We couldn't refresh messages."
              onRetry={() => {
                void retryMessagesQuery();
              }}
            />
          </Box>
        ) : null}

        {isLoadingSavedMessages && !isStreamingCurrentConversation ? (
          <QueryLoadingState
            label={`Loading messages${selectedConversation?.title ? ` for "${selectedConversation.title}"` : ''}...`}
            minHeight="100%"
          />
        ) : blockingMessagesError && !isStreamingCurrentConversation ? (
          <Box px={2}>
            <QueryErrorState
              title="Unable to load messages"
              message={blockingMessagesError}
              onRetry={() => {
                void retryMessagesQuery();
              }}
              minHeight="100%"
            />
          </Box>
        ) : messages.length === 0 && !isStreamingCurrentConversation ? (
          // Empty state — show agent-specific welcome screen
          selectedAgent === 'researcher'
            ? <ResearcherWelcome />
            : (selectedConversation
                ? <DefaultWelcome />
                : <QueryEmptyState title="No messages yet" description="Start the conversation by sending a message." minHeight="100%" />
                )
        ) : (
          messages.map((msg, index) => {
            const processedMessage = processMessageContent(msg);
            const isSystem = processedMessage.sender === 'system';
            const isUser = processedMessage.sender !== 'assistant' && processedMessage.sender !== 'system';
            
            // Check if this is the last assistant message and we're currently streaming
            const isLastAssistantMessage = 
              processedMessage.sender === 'assistant' && 
              index === messages.length - 1 && 
              isStreamingCurrentConversation;
            
            // Check if this is the last assistant message (for cancelled state - doesn't require isStreaming)
            const isLastAssistant = 
              processedMessage.sender === 'assistant' && 
              index === messages.length - 1;

            // Use streaming content for the last assistant message if streaming
            const rawContent = isLastAssistantMessage && streamingMessage 
              ? streamingMessage // Keep newlines, don't trim
              : processedMessage.text; // Keep newlines, don't trim
              
            // Parse export ready data from assistant messages (supports multiple exports)
            const exportDataList = !isUser ? parseAllExportReady(rawContent) : [];
            
            // Strip export markers from content for display
            let displayContent = exportDataList.length > 0 ? stripExportMarker(rawContent) : rawContent;
            
            // Fix broken bold markdown: trailing spaces before closing ** break CommonMark rendering
            // e.g., "**1. Subject here **" → "**1. Subject here**"
            // CRITICAL: content must start with \w (word char) to prevent cross-span accidents.
            // Without \w, the regex matches the CLOSING ** of one span as the opening **
            // of a new (cross-span) match and eats the space before the next bold span.
            // e.g., "**A**, text **B**" would wrongly match "**, text **" → "**,text**".
            // [^*\n] excludes newlines — without \n the regex crosses line boundaries.
            if (!isUser) {
              displayContent = displayContent.replace(/\*\*(\w[^*\n]*?)\s+\*\*/g, '**$1**');
              // Ensure a blank line before a GFM table block when it immediately follows
              // non-table text. remark-gfm requires a blank line before the first table row.
              // Only matches a non-pipe line directly followed by a pipe line (text→table boundary).
              // Does NOT fire between table rows themselves (both start with |).
              displayContent = displayContent.replace(/^([^|\n][^\n]*)\n(\|)/gm, '$1\n\n$2');
              // Escape <email@domain> in From: lines to prevent autolink parsing
              // that can corrupt paragraph structure when rehypeRaw re-parses the HAST.
              // Backslash-escaped angle brackets render as literal < > in CommonMark.
              displayContent = displayContent.replace(/<([^>\s]+@[^>\s]+)>/g, '\\<$1\\>');
              // Ensure blank line before numbered bold entries.
              // Handles BOTH formats: **N.** Subject  AND  **N. Subject**
              // (model may use either depending on the skill prompt version)
              displayContent = displayContent.replace(/([^\n])\n(\*\*\d+\.)/g, '$1\n\n$2');
              // Fix: react-markdown/remark drops the space before an opening ** bold marker
              // when it appears mid-sentence (e.g. "while **assembly**" renders as
              // "whileassembly"). Replace the space with a non-breaking space (U+00A0).
              // Only applied when ** is followed by a letter or digit — for those cases
              // U+00A0 reliably satisfies CommonMark's left-flanking condition 2a.
              displayContent = displayContent.replace(/(\S) (\*\*[a-zA-Z0-9\u0080-\uFFFF])/g, '$1\u00a0$2');
              // Fix: remark/micromark fails to parse ** as a bold opener when content
              // starts with certain ASCII punctuation characters ($, ~, €, £, ¥, #, %).
              // Even U+00A0 doesn't satisfy condition 2b in all micromark builds —
              // the ** gets partially consumed as a delimiter attempt, swallowing the
              // preceding space along with it. Use explicit <strong> HTML tags instead;
              // rehypeRaw handles inline HTML so this renders identically.
              // e.g. "reach **~$132B**" → "reach <strong>~$132B</strong>"
              // e.g. "roughly **$132B by 2030 with ~5.9% CAGR**" → "<strong>...</strong>"
              displayContent = displayContent.replace(
                / \*\*([$~€£¥#%])((?:[^*\n])*?)\*\*/g,
                ' <strong>$1$2</strong>'
              );
            }
            
            // During streaming, hide any incomplete table block at the end of content
            // to prevent remark-gfm from rendering raw pipe characters as plain text.
            if (isLastAssistantMessage && isStreamingCurrentConversation) {
              displayContent = bufferIncompleteStreamingTable(displayContent);
            }

            // Convert citation markers to superscript format [N]
            // Supports multiple formats: [EXCERPT 1], (Excerpt 1) → [1]
            if (!isUser && processedMessage.citations && processedMessage.citations.length > 0) {
              // Pattern 1: [EXCERPT N] - preferred format
              displayContent = displayContent.replace(/\[EXCERPT\s+(\d+)\]/gi, (match, num) => {
                const citationIndex = parseInt(num) - 1;
                if (citationIndex >= 0 && citationIndex < processedMessage.citations!.length) {
                  return `<sup>[${num}]</sup>`;
                }
                return match;
              });
              
              // Pattern 2: (Excerpt N) - alternative format
              displayContent = displayContent.replace(/\(Excerpt\s+(\d+)\)/gi, (match, num) => {
                const citationIndex = parseInt(num) - 1;
                if (citationIndex >= 0 && citationIndex < processedMessage.citations!.length) {
                  return `<sup>[${num}]</sup>`;
                }
                return match;
              });
            }
            
              // <-- https://www.npmjs.com/package/react-markdown
              // If you write actual markdown in your code, put your markdown in a variable;
              // **do not indent markdown**:
              
            return (
              <Box 
                key={processedMessage.id}
                sx={{
                  mb: 2, // Reduced bottom margin (was 3)
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                {/* Date divider - show for first message or when date changes */}
                {(index === 0 || (index > 0 && hasDateChanged(messages[index - 1].created_at, processedMessage.timestamp))) && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2, 
                    my: 2,
                    px: 2
                  }}>
                    <Box sx={{ 
                      flex: 1, 
                      height: '1px', 
                      bgcolor: (theme) => theme.palette.mode === 'light' 
                        ? 'rgba(0, 0, 0, 0.12)' 
                        : 'rgba(255, 255, 255, 0.12)' 
                    }} />
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'text.secondary',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {getDateString(processedMessage.timestamp)}
                    </Typography>
                    <Box sx={{ 
                      flex: 1, 
                      height: '1px', 
                      bgcolor: (theme) => theme.palette.mode === 'light' 
                        ? 'rgba(0, 0, 0, 0.12)' 
                        : 'rgba(255, 255, 255, 0.12)' 
                    }} />
                  </Box>
                )}
                {isSystem && processedMessage.isProcessing ? (
                  <Box 
                    display="flex" 
                    alignItems="center" 
                    justifyContent="center"
                    p={2}
                    mb={2}
                    sx={{
                      background: theme.palette.mode === 'light' 
                        ? 'rgba(0, 0, 0, 0.05)' 
                        : 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(5px)',
                    }}
                  >
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    <Typography color="primary">{processedMessage.text}</Typography>
                  </Box>
                ) : (
                  <Box 
                    display="flex"
                    alignItems="flex-start"
                    flexDirection={isUser ? 'row-reverse' : 'row'}
                    data-message-role={isUser ? 'user' : 'assistant'}
                  >
                    <Avatar 
                      src={isUser ? (userPictureUrl || undefined) : (!isSystem ? getImageUrl('chatbot.png') : undefined)}
                      sx={{ 
                        bgcolor: isUser 
                          ? 'primary.main' 
                          : theme.palette.mode === 'light' 
                            ? 'secondary.light' 
                            : 'secondary.light', // Use a light background for the agent icon in both modes
                        mr: isUser ? 0 : 1,
                        ml: isUser ? 1 : 0,
                        mt: 0.25, // Added a small top margin to align with smaller bubbles
                        color: theme.palette.getContrastText(isUser ? theme.palette.primary.main : theme.palette.secondary.light),
                        width: 32,
                        height: 32,
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)', // Same shadow for both user and agent avatars
                      }}
                    >
                      {isSystem ? 'S' : (isUser && !userPictureUrl ? user?.name?.charAt(0)?.toUpperCase() || 'U' : null)}
                    </Avatar>
                    <Box 
                      data-message-content
                      sx={{
                        position: 'relative',
                        maxWidth: '80%',
                        width: 'fit-content',
                        minWidth: 0, // Allow shrinking below content size
                        '&:hover .message-actions': {
                          opacity: 1,
                        }
                    }}>
                      <Box sx={{
                        width: '100%',
                        maxWidth: '100%', // Ensure it doesn't exceed parent
                        bgcolor: theme.palette.mode === 'light' 
                          ? isUser ? 'rgba(0, 122, 195, 0.1)' : 'background.paper'
                          : isUser ? 'rgba(0, 122, 195, 0.2)' : 'background.paper',
                        color: theme.palette.mode === 'light' 
                          ? 'text.primary' 
                          : 'rgba(255, 255, 255, 0.9)', // Lighter text for better contrast in dark mode
                        px: 1.5, // Horizontal padding unchanged
                        py: 0.625, // Slightly increased vertical padding for better balance
                        borderRadius: 1,
                        position: 'relative',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          borderStyle: 'solid',
                          ...(isUser ? {
                            right: '-8px',
                            borderWidth: '8px 0 8px 8px',
                            borderColor: `transparent transparent transparent ${
                              theme.palette.mode === 'light' 
                                ? 'rgba(0, 122, 195, 0.1)' 
                                : 'rgba(0, 122, 195, 0.2)'
                            }`,
                          } : {
                            left: '-8px',
                            borderWidth: '8px 8px 8px 0',
                            borderColor: `transparent ${
                              theme.palette.mode === 'light'
                                ? theme.palette.background.paper
                                : theme.palette.background.paper
                            } transparent transparent`,
                          }),
                          top: '9px',
                        },
                        
                      }}>
                        {/* Show Chain of Thought and/or Thinking indicator for streaming assistant messages */}
                        {isLastAssistantMessage && !streamingMessage ? (
                          <Box sx={{ width: '100%' }}>
                            {/* Chain of Thought visualization - shows when there are steps */}
                            {cotSteps.length > 0 && (
                              <ChainOfThought
                                steps={cotSteps}
                                isExpanded={isCotExpanded}
                                onToggleExpanded={() => setIsCotExpanded(!isCotExpanded)}
                                isComplete={isCotComplete}
                              />
                            )}
                            {/* Show thinking indicator when no CoT steps yet */}
                            {cotSteps.length === 0 && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={16} />
                                <Typography variant="body2" color="text.secondary">
                                  Thinking...
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        ) :                         
                        // isUser ? (
                        //   // User messages: render as plain text with preserved newlines
                        //   <Typography 
                        //     variant="body2"
                        //     fontWeight={500}
                        //     sx={{ 
                        //       whiteSpace: 'pre-wrap',
                        //       wordBreak: 'break-word',
                        //       padding: '5px 0',
                        //       fontSize: '1em',
                        //       color: 'inherit' // inherit bubble text color to match agent bubble
                        //     }}
                        //   >
                        //     {displayContent}
                        //   </Typography>
                        // ) :                         
                        (
                          // Assistant messages: render with Markdown
                            <Box sx={{ 
                              '& > *:first-of-type': { mt: 0 }, 
                              '& > *:last-child': { mb: 0 },
                              '& p': { mt: 0, mb: '12px' },      // explicit px to override MUI defaults
                              '& li p': { my: 0, pb: 0 },        // no paragraph margins inside list items
                              width: '100%',
                              maxWidth: '100%',
                              padding: '5px 0',
                              overflow: 'hidden', // Prevent content from overflowing
                              overflowWrap: 'break-word', // Wrap long words
                              wordBreak: 'break-word' // Break long words if needed
                            }}>
                              {/* Show Chain of Thought above streaming content */}
                              {isLastAssistantMessage && cotSteps.length > 0 && (
                                <ChainOfThought
                                  steps={cotSteps}
                                  isExpanded={isCotExpanded}
                                  onToggleExpanded={() => setIsCotExpanded(!isCotExpanded)}
                                  isComplete={isCotComplete}
                                />
                              )}
                              {/* Show completed CoT (collapsed) after streaming ends — session only */}
                              {!isLastAssistantMessage && isLastAssistant && completedCotSteps.length > 0 && (
                                <ChainOfThought
                                  steps={completedCotSteps}
                                  isExpanded={isCompletedCotExpanded}
                                  onToggleExpanded={() => setIsCompletedCotExpanded(!isCompletedCotExpanded)}
                                  isComplete={true}
                                />
                              )}
                              <Markdown 
                                remarkPlugins={[remarkGfm]}
                                rehypePlugins={[rehypeRaw]}
                                components={{
                                  table: ({ children, ...props }) => (
                                    <Box sx={{ 
                                      overflowX: 'auto',
                                      margin: '16px 0',
                                      maxWidth: '100%', // Prevent overflow from parent container
                                      border: `1px solid ${theme.palette.divider}`,
                                      borderRadius: 1,
                                      '&::-webkit-scrollbar': {
                                        height: '8px',
                                      },
                                      '&::-webkit-scrollbar-track': {
                                        background: 'transparent',
                                      },
                                      '&::-webkit-scrollbar-thumb': {
                                        background: theme.palette.mode === 'light'
                                          ? 'rgba(0, 0, 0, 0.2)'
                                          : 'rgba(255, 255, 255, 0.35)',
                                        borderRadius: '4px',
                                      },
                                    }}>
                                      <Box component="table" sx={{ 
                                        borderCollapse: 'collapse',
                                        width: '100%',
                                        minWidth: { xs: '420px', sm: '520px', md: '600px' },
                                      }} {...props}>
                                        {children}
                                      </Box>
                                    </Box>
                                  ),
                                  thead: ({ children, ...props }) => (
                                    <Box component="thead" sx={{
                                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'
                                    }} {...props}>
                                      {children}
                                    </Box>
                                  ),
                                  tbody: ({ children, ...props }) => (
                                    <Box component="tbody" {...props}>
                                      {children}
                                    </Box>
                                  ),
                                  tr: ({ children, ...props }) => (
                                    <Box component="tr" sx={{
                                      borderBottom: `1px solid ${theme.palette.divider}`,
                                      '&:last-child': { borderBottom: 'none' }
                                    }} {...props}>
                                      {children}
                                    </Box>
                                  ),
                                  th: ({ children, ...props }) => (
                                    <Box component="th" sx={{
                                      padding: '12px 16px',
                                      textAlign: 'left',
                                      fontWeight: 'bold',
                                      borderRight: `1px solid ${theme.palette.divider}`,
                                      '&:last-child': { borderRight: 'none' },
                                      minWidth: { xs: '120px', sm: '140px', md: '160px' },
                                      maxWidth: { xs: '220px', sm: '260px', md: '300px' }
                                    }} {...props}>
                                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                        {children}
                                      </Typography>
                                    </Box>
                                  ),
                                  td: ({ children, ...props }) => (
                                    <Box component="td" sx={{
                                      padding: '12px 16px',
                                      borderRight: `1px solid ${theme.palette.divider}`,
                                      '&:last-child': { borderRight: 'none' },
                                      minWidth: { xs: '120px', sm: '140px', md: '160px' },
                                      maxWidth: { xs: '220px', sm: '260px', md: '300px' }
                                    }} {...props}>
                                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {children}
                                      </Typography>
                                    </Box>
                                  )
                                  ,
                                  // Ensure paragraph text preserves newlines (e.g., pasted text)
                                  p: ({ children, ...props }) => (
                                    <Box component="p" sx={{ my: 0.25, paddingBottom: 0.1, whiteSpace: 'pre-wrap', lineHeight: 1.4, color: 'inherit', fontFamily: 'Arial, Inter, sans-serif', wordBreak: 'break-word', overflowWrap: 'break-word' }} {...props}>
                                      {children}
                                    </Box>
                                  ),
                                  // Handle line breaks - render as actual <br> element
                                  br: () => <br />,
                                  // Style heading elements with responsive font sizes for mobile
                                  h1: ({ children, ...props }) => (
                                    <Typography 
                                      variant="h4" 
                                      component="h1" 
                                      sx={{ 
                                        mt: 2, 
                                        mb: 1.5, 
                                        fontWeight: 600,
                                        fontSize: { xs: '1.25rem', sm: '1.75rem' } // Smaller on mobile
                                      }} 
                                      {...props}
                                    >
                                      {children}
                                    </Typography>
                                  ),
                                  h2: ({ children, ...props }) => (
                                    <Typography 
                                      variant="h5" 
                                      component="h2" 
                                      sx={{ 
                                        mt: 1.75, 
                                        mb: 1.25, 
                                        fontWeight: 600,
                                        fontSize: { xs: '1.1rem', sm: '1.5rem' } // Smaller on mobile
                                      }} 
                                      {...props}
                                    >
                                      {children}
                                    </Typography>
                                  ),
                                  h3: ({ children, ...props }) => (
                                    <Typography 
                                      variant="h6" 
                                      component="h3" 
                                      sx={{ 
                                        mt: 1.5, 
                                        mb: 1, 
                                        fontWeight: 600,
                                        fontSize: { xs: '1rem', sm: '1.25rem' } // Smaller on mobile
                                      }} 
                                      {...props}
                                    >
                                      {children}
                                    </Typography>
                                  ),
                                  h4: ({ children, ...props }) => (
                                    <Typography 
                                      variant="subtitle1" 
                                      component="h4" 
                                      sx={{ 
                                        mt: 1.25, 
                                        mb: 0.75, 
                                        fontWeight: 600,
                                        fontSize: { xs: '0.95rem', sm: '1.1rem' } // Smaller on mobile
                                      }} 
                                      {...props}
                                    >
                                      {children}
                                    </Typography>
                                  ),
                                  h5: ({ children, ...props }) => (
                                    <Typography 
                                      variant="subtitle2" 
                                      component="h5" 
                                      sx={{ 
                                        mt: 1, 
                                        mb: 0.5, 
                                        fontWeight: 600,
                                        fontSize: { xs: '0.9rem', sm: '1rem' } // Smaller on mobile
                                      }} 
                                      {...props}
                                    >
                                      {children}
                                    </Typography>
                                  ),
                                  h6: ({ children, ...props }) => (
                                    <Typography 
                                      variant="subtitle2" 
                                      component="h6" 
                                      sx={{ 
                                        mt: 1, 
                                        mb: 0.5, 
                                        fontWeight: 600,
                                        fontSize: { xs: '0.85rem', sm: '0.95rem' } // Smaller on mobile
                                      }} 
                                      {...props}
                                    >
                                      {children}
                                    </Typography>
                                  ),
                                  // Style list elements
                                  ul: ({ children, ...props }) => (
                                    <Box component="ul" sx={{ my: 0, mb: 2.25, pl: 7 }} {...props}>
                                      {children}
                                    </Box>
                                  ),
                                  ol: ({ children, ...props }) => (
                                    <Box component="ol" sx={{ my: 0, mb: 0.75, ml: 0 }} {...props}>
                                      {children}
                                    </Box>
                                  ),
                                  li: ({ children, ...props }) => (
                                    <Box component="li" sx={{ my: 0.5, pl: 0, lineHeight: 1.4, fontFamily: 'Arial, Inter, sans-serif' }} {...props}>
                                      {children}
                                    </Box>
                                  ),
                                  // Style inline code elements
                                  code: ({ inline, children, ...props }: any) => {
                                    if (inline) {
                                      return (
                                        <Box 
                                          component="code" 
                                          sx={{ 
                                            fontFamily: 'monospace',
                                            fontSize: '0.85em',
                                            backgroundColor: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.1)',
                                            padding: '2px 6px',
                                            borderRadius: '3px',
                                            wordBreak: 'break-word',
                                            overflowWrap: 'break-word'
                                          }} 
                                          {...props}
                                        >
                                          {children}
                                        </Box>
                                      );
                                    }
                                    // Block code
                                    return (
                                      <Box 
                                        component="code" 
                                        sx={{ 
                                          fontFamily: 'monospace',
                                          fontSize: '0.875rem',
                                          display: 'block',
                                          whiteSpace: 'pre',
                                          overflowX: 'auto',
                                          wordBreak: 'normal',
                                          overflowWrap: 'normal'
                                        }} 
                                        {...props}
                                      >
                                        {children}
                                      </Box>
                                    );
                                  },
                                  // Style links with appropriate color for dark mode
                                  a: ({ children, href, ...props }) => (
                                    <a
                                      href={href}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{
                                        color: theme.palette.mode === 'dark' ? '#64b5f6' : '#007AC2',
                                        textDecoration: 'none',
                                        textUnderlineOffset: '2px'
                                      }}
                                      {...props}
                                    >
                                      {children}
                                    </a>
                                  ),
                                  // Style pre (code block container)
                                  pre: ({ children, ...props }) => {
                                    // Extract the code element from children
                                    const codeChild = React.Children.toArray(children).find(
                                      (child: any) => child?.props?.node?.tagName === 'code'
                                    ) as any;
                                    const codeText: string = codeChild ? String(codeChild.props?.children || '') : '';

                                    // Detect downloadable file types by the fenced code block language tag
                                    // e.g. ```ics, ```csv, ```vcf, ```json
                                    const DOWNLOADABLE_TYPES: Record<string, { mime: string; ext: string; label: string }> = {
                                      ics:  { mime: 'text/calendar',       ext: 'ics',  label: 'Download .ics' },
                                      csv:  { mime: 'text/csv',            ext: 'csv',  label: 'Download .csv' },
                                      vcf:  { mime: 'text/vcard',          ext: 'vcf',  label: 'Download .vcf' },
                                      json: { mime: 'application/json',    ext: 'json', label: 'Download .json' },
                                    };
                                    const langClass: string = codeChild?.props?.className || '';
                                    const langMatch = langClass.match(/language-(\w+)/);
                                    const lang = langMatch ? langMatch[1].toLowerCase() : '';
                                    const downloadMeta = DOWNLOADABLE_TYPES[lang];

                                    const handleDownloadFile = () => {
                                      if (!downloadMeta) return;
                                      const blob = new Blob([codeText], { type: downloadMeta.mime });
                                      const url = URL.createObjectURL(blob);
                                      const a = document.createElement('a');
                                      a.href = url;
                                      a.download = `download.${downloadMeta.ext}`;
                                      a.click();
                                      URL.revokeObjectURL(url);
                                    };

                                    const isCopied = copiedCode === codeText;

                                    return (
                                      <Box 
                                        sx={{ 
                                          position: 'relative',
                                          margin: '8px 0',
                                          '&:hover .copy-button': {
                                            opacity: 1
                                          },
                                          '&:hover .download-button': {
                                            opacity: 1
                                          }
                                        }}
                                      >
                                        {/* Download button — only shown for recognised file types */}
                                        {downloadMeta && (
                                          <Tooltip title={downloadMeta.label}>
                                            <span>
                                              <IconButton
                                                className="download-button"
                                                size="small"
                                                onClick={handleDownloadFile}
                                                sx={{
                                                  position: 'absolute',
                                                  top: '8px',
                                                  right: '44px',
                                                  opacity: 0,
                                                  transition: 'opacity 0.2s',
                                                  backgroundColor: theme.palette.mode === 'light'
                                                    ? 'rgba(255, 255, 255, 0.9)'
                                                    : 'rgba(0, 0, 0, 0.6)',
                                                  '&:hover': {
                                                    backgroundColor: theme.palette.mode === 'light'
                                                      ? 'rgba(255, 255, 255, 1)'
                                                      : 'rgba(0, 0, 0, 0.8)',
                                                  },
                                                  zIndex: 1
                                                }}
                                              >
                                                <FileDownloadOutlinedIcon fontSize="small" />
                                              </IconButton>
                                            </span>
                                          </Tooltip>
                                        )}
                                        <Tooltip title={isCopied ? "Copied!" : "Copy code"}>
                                          <span>
                                            <IconButton
                                            className="copy-button"
                                            size="small"
                                            onClick={() => handleCopyCode(codeText)}
                                            sx={{
                                              position: 'absolute',
                                              top: '8px',
                                              right: '8px',
                                              opacity: 0,
                                              transition: 'opacity 0.2s',
                                              backgroundColor: theme.palette.mode === 'light' 
                                                ? 'rgba(255, 255, 255, 0.9)' 
                                                : 'rgba(0, 0, 0, 0.6)',
                                              '&:hover': {
                                                backgroundColor: theme.palette.mode === 'light' 
                                                  ? 'rgba(255, 255, 255, 1)' 
                                                  : 'rgba(0, 0, 0, 0.8)',
                                              },
                                              zIndex: 1
                                            }}
                                          >
                                            {isCopied ? (
                                              <CheckIcon fontSize="small" sx={{ color: 'success.main' }} />
                                            ) : (
                                              <ContentCopyIcon fontSize="small" />
                                            )}
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                        <Box 
                                          component="pre" 
                                          sx={{ 
                                            backgroundColor: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.1)',
                                            padding: '12px',
                                            borderRadius: '6px',
                                            overflowX: 'auto',
                                            maxWidth: '100%',
                                            '&::-webkit-scrollbar': {
                                              height: '8px',
                                            },
                                            '&::-webkit-scrollbar-track': {
                                              background: 'transparent',
                                            },
                                            '&::-webkit-scrollbar-thumb': {
                                              background: theme.palette.mode === 'light'
                                                ? 'rgba(0, 0, 0, 0.2)'
                                                : 'rgba(255, 255, 255, 0.35)',
                                              borderRadius: '4px',
                                            },
                                          }} 
                                          {...props}
                                        >
                                          {children}
                                        </Box>
                                      </Box>
                                    );
                                  }
                                }}
                              >
                                {replaceNewlines(displayContent)}
                              </Markdown>
                              
                              {/* Show cancellation message if user stopped streaming or loaded a cancelled message */}
                              {!isUser && ((isLastAssistant && streamingCancelledForUI) || msg.was_cancelled) && (
                                <Box
                                  sx={{
                                    mt: displayContent?.trim() ? 1 : 0,
                                    p: 1.5,
                                    backgroundColor: theme.palette.mode === 'light'
                                      ? 'rgba(255, 152, 0, 0.08)'
                                      : 'rgba(255, 152, 0, 0.15)',
                                    border: `1px solid ${theme.palette.mode === 'light' ? 'rgba(255, 152, 0, 0.3)' : 'rgba(255, 152, 0, 0.4)'}`,
                                    borderRadius: '6px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                  }}
                                >
                                  <StopIcon sx={{ fontSize: '1rem', color: 'warning.main' }} />
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: 'warning.main',
                                      fontStyle: 'italic',
                                      fontSize: '0.85rem'
                                    }}
                                  >
                                    {displayContent?.trim() 
                                      ? 'Response cancelled by user. Partial response shown above.'
                                      : 'Response cancelled by user before any content was received.'}
                                  </Typography>
                                </Box>
                              )}
                              
                              {/* Render export download button(s) if agent generated exports */}
                              {exportDataList.length > 0 && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                                  {exportDataList.map((exportData, idx) => (
                                    <ExportDownloadButton key={exportData.id || idx} exportData={exportData} />
                                  ))}
                                </Box>
                              )}
                            </Box>
                        )
                        }
                        {/* Show typing indicator only for streaming assistant messages */}
                        {/* {isLastAssistantMessage && isStreaming && streamingMessage && (
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                            <CircularProgress size={12} sx={{ mr: 1 }} />
                            <Typography variant="caption" color="text.secondary">
                              Streaming...
                            </Typography>
                          </Box>
                        )} */}
                        
                        {isUser && msg.contains_attachment && msg.file_names && Array.isArray(msg.file_names) && (
                          renderAttachedFiles(msg.file_names, msg.onedrive_urls)
                        )}

                        {/* Show citations/sources for assistant messages only */}
                        {!isUser && processedMessage.citations && processedMessage.citations.length > 0 && (
                          <MessageCitations citations={processedMessage.citations} />
                        )}
                        
                        {/* Show streaming citations for the last assistant message during streaming */}
                        {!isUser && isLastAssistantMessage && isStreamingCurrentConversation && streamingCitations.length > 0 && (
                          <MessageCitations citations={streamingCitations} />
                        )}
                        
                        {/* Message timestamp */}
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            display: 'block',
                            mt: 0.5,
                            color: theme.palette.mode === 'light'
                              ? 'rgba(0, 0, 0, 0.5)'
                              : 'rgba(255, 255, 255, 0.5)',
                            fontSize: '0.7rem',
                            textAlign: isUser ? 'right' : 'left'
                          }}
                        >
                          {getTimeString(processedMessage.timestamp)}
                        </Typography>
                      </Box>
                      {!isUser && !isLastAssistantMessage && (
                        <Box
                          sx={{ 
                            display: 'flex',
                            justifyContent: 'flex-start',
                            
                          }}
                        >
                          <MessageActions
                            messageId={msg.id}
                            conversationId={msg.conversationId}
                            text={processedMessage.text}
                            reaction={msg.reaction}
                            modelName={(!selectedAgent || selectedAgent === 'default') ? (msg.model_name || msg.model_key) : undefined}
                            requestedMode={msg.requested_mode}
                            effectiveMode={msg.effective_mode}
                            onReactionChange={() => loadMessages(selectedConversation?.id || '')}
                            onShowSnackbar={handleShowSnackbar}
                          />
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })
        )}
        
        <div ref={messagesEndRef} />
      </Box>
      <Box 
        sx={{ 
          position: 'fixed',
          bottom: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '100%',
          maxWidth: '1200px',
          px: 2,
          pt: 3,
          pb: 0.9,
          pr: 2,
          backgroundColor: 'transparent',
          zIndex: 10
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <Box sx={{ flex: 1 }}>
            <ChatInput 
              ref={chatInputRef}
              conversationId={selectedConversation?.id || ''} 
              onMessageSent={() => {}} 
              onBeforeSend={addUserMessage}
              disabled={isStreamingCurrentConversation}
            />
          </Box>
          {isStreamingCurrentConversation && (
            <Tooltip title="Stop streaming response">
              <IconButton
                onClick={stopStreaming}
                sx={{
                  backgroundColor: theme.palette.mode === 'light' 
                    ? 'rgba(244, 67, 54, 0.1)' 
                    : 'rgba(244, 67, 54, 0.2)',
                  color: 'error.main',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'light' 
                      ? 'rgba(244, 67, 54, 0.2)' 
                      : 'rgba(244, 67, 54, 0.3)',
                  },
                  mb: 0.5,
                  width: 40,
                  height: 40,
                }}
                aria-label="stop streaming"
              >
                <StopIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        
        {/* Main Cards - Always visible below input */}
        {/* Researcher uses the compact MainCards style; other agents use the full AgentCards hero */}
        {(selectedAgent && selectedAgent !== 'default' && selectedAgent !== 'researcher')
          ? <AgentCards />
          : <MainCards cards={selectedAgent === 'researcher' ? researcherCards : undefined} />}
      </Box>
      
      {/* Snackbar for feedback notifications */}
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      
      </Box> {/* Close the centered container */}
    </Box>
  );
};